import { useId, useState } from "react";
import { Reveal } from "../Reveal";
import { KALQY_CONTACT, SECTION_IDS, type MarketingCopy } from "../copy";
import { MailIcon, MinusIcon, PhoneIcon, PlusIcon } from "../icons";
import { Section, SectionHeading } from "./primitives";

export interface FaqProps {
  t: MarketingCopy;
}

/**
 * The questions a school asks before it books a demo, answered on the page.
 *
 * ⚠️ ONE OPEN AT A TIME, AND THE FIRST ONE OPEN. The first question — "what do
 * we need?" — is the one every reader has, so it starts answered; opening
 * another closes it, so the column never grows into a wall of text.
 *
 * ⚠️ A DISCLOSURE, NOT AN ARIA "ACCORDION" WIDGET. Each question is a real
 * `<button>` with `aria-expanded` and `aria-controls`, and each answer is a
 * labelled `region` — the pattern every screen reader already understands, with
 * no roving-tabindex keyboard model to get wrong.
 */
export function Faq({ t }: FaqProps) {
  const [open, setOpen] = useState(0);
  const base = useId();

  return (
    <Section id={SECTION_IDS.faq} className="pt-24 lg:pt-32">
      <div className="grid items-start gap-10 lg:grid-cols-[0.75fr_1.25fr] lg:gap-16">
        <div>
          <SectionHeading label={t.faq.label} heading={t.faq.heading} accentClass="bg-primary" labelClass="text-primary" />
          <Reveal delay={90}>
            <div className="mt-8 rounded-xl bg-primary-tint p-6 sm:p-7">
              <h3 className="font-dash text-dash-lg font-extrabold text-ink">{t.faq.contactHeading}</h3>
              <p className="mt-1.5 font-dash text-dash-sm text-ink-soft">{t.faq.contactBody}</p>
              <ul className="mt-4 space-y-2">
                <li>
                  <a
                    href={`mailto:${KALQY_CONTACT.email}`}
                    className="inline-flex items-center gap-2.5 rounded-md font-dash text-dash-sm font-extrabold text-ink outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <MailIcon className="h-4.5 w-4.5 text-primary" />
                    {KALQY_CONTACT.email}
                  </a>
                </li>
                <li>
                  <a
                    href={KALQY_CONTACT.phoneHref}
                    className="tabular inline-flex items-center gap-2.5 rounded-md font-dash text-dash-sm font-extrabold text-ink outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <PhoneIcon className="h-4.5 w-4.5 text-primary" />
                    {KALQY_CONTACT.phone}
                  </a>
                </li>
              </ul>
            </div>
          </Reveal>
        </div>

        <ul className="space-y-3">
          {t.faq.items.map((item, i) => {
            const isOpen = open === i;
            const buttonId = `${base}-q${String(i)}`;
            const panelId = `${base}-a${String(i)}`;
            return (
              <li key={item.q}>
                <Reveal delay={i * 50}>
                  <div
                    className={`rounded-xl border transition-colors duration-200 ease-dash ${
                      isOpen ? "border-primary-tint bg-cream ring-2 ring-primary-tint" : "border-line bg-surface hover:border-line-strong"
                    }`}
                  >
                    <h3>
                      <button
                        id={buttonId}
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => {
                          setOpen(isOpen ? -1 : i);
                        }}
                        className="flex w-full items-center justify-between gap-4 rounded-xl px-5 py-4 text-left font-dash text-dash-md font-extrabold text-ink outline-none focus-visible:ring-2 focus-visible:ring-primary sm:px-6 sm:py-5"
                      >
                        {item.q}
                        <span
                          aria-hidden="true"
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-pill border transition-colors duration-150 ease-dash ${
                            isOpen ? "border-primary bg-primary text-primary-ink" : "border-line bg-canvas text-ink"
                          }`}
                        >
                          {isOpen ? <MinusIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                        </span>
                      </button>
                    </h3>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      hidden={!isOpen}
                      className="px-5 pb-5 font-dash text-dash-sm text-ink-soft sm:px-6 sm:pb-6"
                    >
                      {item.a}
                    </div>
                  </div>
                </Reveal>
              </li>
            );
          })}
        </ul>
      </div>
    </Section>
  );
}
